import { logger } from "@/utils/debug";
import type { WorkspaceStore } from "@/types/workspace/Store";
import { useMapStore } from "@/stores/map";
import { useUIStore } from "@/stores/ui";
import { useDataflowStore } from "@/stores/dataflow";
import { useDataProcessingStore } from "@/stores/data-processing";
import type { MapViewState } from "@deck.gl/core";
import { create } from "zustand";
import { devtools } from "zustand/middleware";

export const useWorkspaceStore = create<WorkspaceStore>()(
  devtools(
    () => ({
      execute: async (dataflowId, actions) => {
        if (!dataflowId) {
          logger.warn("[WorkspaceActions] No dataflowId provided");
          return;
        }

        for (const actionItem of actions) {
          const { context, action, data = {} } = actionItem;
          try {
            if (context === "map") {
              await executeMapAction(dataflowId, action, data);
            } else if (context === "workspace") {
              executeWorkspaceAction(dataflowId, action, data);
            } else if (context === "dataflow") {
              await executeDataflowAction(dataflowId, action, data);
            } else if (context === "category") {
              executeCategoryAction(dataflowId, action, data);
            }
          } catch (error) {
            throw error;
          }
        }
      },
    }),
    {
      name: "workspace-store",
    }
  )
);

async function executeMapAction(
  dataflowId: string,
  action: string,
  data: Record<string, any>
) {
  const mapStore = useMapStore.getState();

  if (action === "fly") {
    const currentViewState = mapStore.getViewState(dataflowId);
    if (!currentViewState) {
      logger.warn(`[MapActions] No viewState found for workspace ${dataflowId}`);
      return;
    }

    const { zoom, location, center } = data;
    if (!center) {
      logger.warn("[MapActions] fly requires 'center' [lng,lat]");
      return;
    }

    const zoomLevel = zoom ?? currentViewState.zoom;
    const newViewState: MapViewState = {
      ...currentViewState,
      longitude: center[0],
      latitude: center[1],
      zoom: zoomLevel,
      transitionDuration: 1000,
      transitionEasing: (t: number) => t * (2 - t),
    };

    mapStore.setViewState(dataflowId, newViewState);

    if (location) {
      await mapStore.highlightArea(dataflowId, { placeName: location });
    }
  } else if (action === "toggleLayer") {
    const { layerId, visible } = data;
    if (layerId && typeof visible === "boolean") {
      mapStore.setStaticLayerVisibility(layerId, visible);
    }
  } else {
    logger.warn(`[MapActions] Unknown action: ${action}`);
  }
}

function executeWorkspaceAction(
  dataflowId: string,
  action: string,
  data: Record<string, any>
) {
  const uiStore = useUIStore.getState();

  if (action === "setView") {
    const { view } = data;
    if (view) {
      uiStore.setView(dataflowId, view);
    }
  } else if (action === "openSidebar") {
    const { mode } = data;
    if (mode) {
      uiStore.openSidebar(dataflowId, mode as any);
    }
  } else if (action === "closeSidebar") {
    uiStore.closeSidebar(dataflowId);
  } else if (action === "openModal") {
    const { mode, title, modalData } = data;
    if (mode) {
      uiStore.openModal(dataflowId, mode as any, title, modalData);
    }
  } else if (action === "closeModal") {
    uiStore.closeModal(dataflowId);
  }
}

async function executeDataflowAction(
  dataflowId: string,
  action: string,
  data: Record<string, any>
) {
  const dataflowStore = useDataflowStore.getState();
  const dataflow = dataflowStore.dataflows[dataflowId];

  if (!dataflow) {
    logger.warn("[DataflowActions] No dataflow found for dataflowId:", dataflowId);
    return;
  }

  if (action === "addTransformer") {
    const { transformer } = data;
    if (!transformer) {
      logger.warn("[DataflowActions] No transformer provided in addTransformer action");
      return;
    }

    const existingTransformers = dataflow.flow_definition_json || [];
    const newTransformers = [...existingTransformers, transformer];

    dataflowStore.updateDataflow(dataflowId, {
      flow_definition_json: newTransformers,
    });

    const hasDataInput = newTransformers.some(
      (t: any) => t.type === "user_file" || t.type === "data_input"
    );
    if (hasDataInput) {
      const { executeDataflow } = useDataProcessingStore.getState();
      const updatedDataflow = {
        ...dataflow,
        flow_definition_json: newTransformers,
      };
      executeDataflow(dataflowId, updatedDataflow).catch((error) =>
        logger.error(error)
      );
    }
  } else if (action === "updateTransformers") {
    const { transformers } = data;
    if (!Array.isArray(transformers)) {
      logger.warn("[DataflowActions] transformers must be an array");
      return;
    }

    dataflowStore.updateDataflow(dataflowId, {
      flow_definition_json: transformers,
    });

    const hasDataInput = transformers.some(
      (t: any) => t.type === "user_file" || t.type === "data_input"
    );
    if (hasDataInput) {
      const { executeDataflow } = useDataProcessingStore.getState();
      const updatedDataflow = {
        ...dataflow,
        flow_definition_json: transformers,
      };
      executeDataflow(dataflowId, updatedDataflow).catch((error) =>
        logger.error(error)
      );
    }
  } else if (action === "updateMetadata") {
    const { dataflow_name, dataflow_description } = data;
    const updates: any = {};
    if (dataflow_name !== undefined) {
      updates.dataflow_name = dataflow_name;
    }
    if (dataflow_description !== undefined) {
      updates.dataflow_description = dataflow_description;
    }
    if (Object.keys(updates).length > 0) {
      dataflowStore.updateDataflow(dataflowId, updates);
    }
  } else {
    logger.warn(`[DataflowActions] Unknown action: ${action}`);
  }
}

function executeCategoryAction(
  dataflowId: string,
  action: string,
  data: Record<string, any>
) {
  const mapStore = useMapStore.getState();

  if (action === "setColumn") {
    const { columnName } = data;
    if (columnName !== undefined) {
      mapStore.setGroupColumnName(dataflowId, columnName);
    }
  } else if (action === "setConfig") {
    const { category, config } = data;
    if (category && config) {
      mapStore.setGroupConfig(dataflowId, category, {
        name: config.name,
        color: config.color,
      });
    }
  } else if (action === "setVisible") {
    const { categories } = data;
    if (categories !== undefined) {
      mapStore.setVisibleGroups(dataflowId, categories || []);
    }
  } else {
    logger.warn(`[CategoryActions] Unknown action: ${action}`);
  }
}
